/**
 * @fileoverview Landing page metrics display with mock data.
 * Showcases the MetricCards UI without requiring authentication.
 * @module features/landing/components/LandingMetrics
 */

import { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveInfoPanel } from '@/shared/components/ui/ResponsiveInfoPanel'
import { useIsBelowDesktop } from '@/shared/hooks/useIsMobile'
import {
  HoverInfoHeader,
  HoverInfoDivider,
  HoverProductRow,
} from '@/shared/components/ui/HoverInfoCard'
import { ExpandIcon } from '@/shared/components/ui/ExpandIcon'
import { AnimatedCard } from '@/shared/components/ui/AnimatedCard'
import { BackgroundGradientAnimation } from '@/components/ui/background-gradient-animation'
import { cn } from '@/lib/utils'
import { ShoppingCart, BanknoteArrowDown, CircleDollarSign } from 'lucide-react'
import { BsMeta } from 'react-icons/bs'
import { GoogleAdsIcon } from '@/shared/components/ui/icons/GoogleAdsIcon'

/**
 * Mock product data for hover panels.
 */
const MOCK_PRODUCTS = {
  topRevenue: [
    { sku: 'monitor-27', name: 'Monitor 27" 4K', quantity: 24, sales: 68000, adSpend: 2100, image: '/landing-page/monitor-mock.webp' },
    { sku: 'keyboard-pro', name: 'Mechanical Keyboard Pro', quantity: 38, sales: 56000, adSpend: 1600, image: '/landing-page/keyboard-mock.webp' },
    { sku: 'mouse-gaming', name: 'Gaming Mouse Elite', quantity: 62, sales: 42000, adSpend: 800, image: '/landing-page/mouse-mock.webp' },
    { sku: 'headphones-hd', name: 'Wireless Headphones HD', quantity: 35, sales: 32000, adSpend: 22800, image: '/landing-page/headphones-mock.webp' },
  ],
  topAdSpend: [
    { sku: 'headphones-hd', name: 'Wireless Headphones HD', adSpend: 22800, meta: 14800, google: 8000, image: '/landing-page/headphones-mock.webp' },
    { sku: 'studio-monitors', name: 'Studio Monitors 5"', adSpend: 13400, meta: 8700, google: 4700, image: '/landing-page/studiomonitors-mock.webp' },
    { sku: 'controller-pro', name: 'Gaming Controller Pro', adSpend: 9200, meta: 6000, google: 3200, image: '/landing-page/gamingcontroller-mock.webp' },
    { sku: 'powerbank-20k', name: 'Power Bank 20000mAh', adSpend: 3400, meta: 2200, google: 1200, image: '/landing-page/powerbank-mock.webp' },
  ],
  topCogs: [
    { sku: 'monitor-27', name: 'Monitor 27" 4K', quantity: 24, cogs: 22400, image: '/landing-page/monitor-mock.webp' },
    { sku: 'keyboard-pro', name: 'Mechanical Keyboard Pro', quantity: 38, cogs: 12800, image: '/landing-page/keyboard-mock.webp' },
    { sku: 'headphones-hd', name: 'Wireless Headphones HD', quantity: 35, cogs: 12400, image: '/landing-page/headphones-mock.webp' },
    { sku: 'studio-monitors', name: 'Studio Monitors 5"', quantity: 12, cogs: 6200, image: '/landing-page/studiomonitors-mock.webp' },
  ],
  profitable: [
    { sku: 'monitor-27', name: 'Monitor 27" 4K', quantity: 24, profit: 43500, adSpend: 2100, image: '/landing-page/monitor-mock.webp' },
    { sku: 'keyboard-pro', name: 'Mechanical Keyboard Pro', quantity: 38, profit: 41600, adSpend: 1600, image: '/landing-page/keyboard-mock.webp' },
    { sku: 'mouse-gaming', name: 'Gaming Mouse Elite', quantity: 62, profit: 37000, adSpend: 800, image: '/landing-page/mouse-mock.webp' },
    { sku: 'microphone-usb', name: 'USB Microphone Studio', quantity: 14, profit: 6700, adSpend: 400, image: '/landing-page/microphone-mock.webp' },
  ],
  losers: [
    { sku: 'headphones-hd', name: 'Wireless Headphones HD', quantity: 35, profit: -3200, adSpend: 22800, image: '/landing-page/headphones-mock.webp' },
    { sku: 'studio-monitors', name: 'Studio Monitors 5"', quantity: 12, profit: -1600, adSpend: 13400, image: '/landing-page/studiomonitors-mock.webp' },
    { sku: 'controller-pro', name: 'Gaming Controller Pro', quantity: 18, profit: -1500, adSpend: 9200, image: '/landing-page/gamingcontroller-mock.webp' },
    { sku: 'powerbank-20k', name: 'Power Bank 20000mAh', quantity: 6, profit: -1200, adSpend: 3400, image: '/landing-page/powerbank-mock.webp' },
  ],
}

const MOCK_COUNTRIES = [
  { country: 'PL', revenue: 156800 },
  { country: 'CZ', revenue: 54300 },
  { country: 'SK', revenue: 30200 },
]

/**
 * Gradient theme colors for the Profit card background animation.
 */
const GRADIENT_THEMES = {
  positive: {
    gradientBackgroundStart: 'rgb(2, 44, 34)',
    gradientBackgroundEnd: 'rgb(15, 23, 42)',
    firstColor: '6, 78, 59',
    secondColor: '30, 41, 59',
    thirdColor: '5, 150, 105',
    fourthColor: '15, 23, 42',
    fifthColor: '2, 44, 34',
  },
}

/**
 * Mock values for the metrics display.
 */
const MOCK_METRICS = {
  revenue: 241300,
  orders: 209,
  avgOrderValue: 1155,
  adSpend: 53700,
  cogs: 66300,
  businessCosts: 47100,
  totalCosts: 167100,
  profit: 74200,
  margin: 30.7,
}

/**
 * Formats a number as compact currency (e.g., 14.1K zł).
 */
function formatMoney(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K zł`
  }
  return `${value} zł`
}

/**
 * Formats a number with thousands separator.
 */
function formatInt(value: number): string {
  return value.toLocaleString('en-US')
}

/**
 * Returns flag emoji for a country code.
 */
function flagForCountry(code: string): string {
  const upper = code.toUpperCase()
  if (/^[A-Z]{2}$/.test(upper)) {
    const codePoint = (ch: string): number => 0x1f1e6 + (ch.charCodeAt(0) - 65)
    return String.fromCodePoint(codePoint(upper.charAt(0)), codePoint(upper.charAt(1)))
  }
  return ''
}

/**
 * Landing page metrics component with mock data and interactive popovers.
 * Showcases the full MetricCards experience without requiring authentication.
 *
 * @component
 */
export function LandingMetrics(): ReactElement {
  const { t } = useTranslation('features-landing')
  const isBelowDesktop = useIsBelowDesktop()

  /** Sales hover panel content */
  const renderSalesHoverContent = (): ReactNode => (
    <div>
      <HoverInfoHeader
        title={t('demo.labels.mostRevenue')}
        leading={
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: 'var(--color-chart-legend-revenue)' }}
            aria-hidden="true"
          />
        }
        leadingClassName="self-center"
        trailing={<ExpandIcon />}
      />
      <HoverInfoDivider />
      <div>
        {MOCK_PRODUCTS.topRevenue.map(p => (
          <HoverProductRow
            key={`rev-${p.sku}`}
            imageUrl={p.image}
            title={`${formatInt(p.quantity)} × ${p.name}`}
            value={formatMoney(p.sales)}
            leftMeta={`${formatMoney(p.adSpend)} ${t('demo.labels.adSpent')}`}
            dimQuantityPrefix
          />
        ))}
      </div>
      <div className="flex items-center gap-5 px-6 py-3 flex-wrap border-t border-border/30">
        {MOCK_COUNTRIES.slice(0, 2).map(c => (
          <span key={c.country} className="inline-flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
            <span>{flagForCountry(c.country)}</span>
            <span className="font-medium text-foreground/80">{formatMoney(c.revenue)}</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
          <span>🌍</span>
          <span className="font-medium text-foreground/80">
            {formatMoney(MOCK_COUNTRIES.slice(2).reduce((sum, c) => sum + c.revenue, 0))}
          </span>
        </span>
      </div>
    </div>
  )

  /** Ad Spend hover panel content */
  const renderAdSpendHoverContent = (): ReactNode => (
    <div>
      <HoverInfoHeader
        title={t('demo.metrics.adSpend')}
        leading={
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: 'var(--chart-legend-adspend)' }}
            aria-hidden="true"
          />
        }
        leadingClassName="self-center"
        trailing={<ExpandIcon />}
      />
      <HoverInfoDivider />
      <div>
        {MOCK_PRODUCTS.topAdSpend.map(p => (
          <HoverProductRow
            key={`ad-${p.sku}`}
            imageUrl={p.image}
            title={p.name}
            value={formatMoney(p.adSpend)}
            leftMetaLabel={t('demo.labels.topPlatform', { defaultValue: 'Top platform' })}
            leftMeta={
              <span className="flex items-center gap-1">
                <BsMeta className="h-3 w-3" />
                {formatMoney(p.meta)}
              </span>
            }
            rightMetaLabel={t('demo.labels.secondPlatform', { defaultValue: 'Second platform' })}
            rightMeta={
              <span className="flex items-center gap-1">
                <GoogleAdsIcon className="h-3 w-3" />
                {formatMoney(p.google)}
              </span>
            }
          />
        ))}
      </div>
      <div className="flex items-center gap-5 px-6 py-3 flex-wrap border-t border-border/30">
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
          <BsMeta className="h-3.5 w-3.5" />
          <span className="font-medium text-foreground/80">{formatMoney(34900)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
          <GoogleAdsIcon className="h-3.5 w-3.5" />
          <span className="font-medium text-foreground/80">{formatMoney(18800)}</span>
        </span>
      </div>
    </div>
  )

  /** COGS hover panel content */
  const renderCogsHoverContent = (): ReactNode => (
    <div className="pb-3">
      <HoverInfoHeader
        title={t('demo.metrics.cogs')}
        leading={
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: 'var(--chart-legend-cogs)' }}
            aria-hidden="true"
          />
        }
        leadingClassName="self-center"
        trailing={<ExpandIcon />}
      />
      <HoverInfoDivider />
      <div>
        {MOCK_PRODUCTS.topCogs.map(p => (
          <HoverProductRow
            key={`cogs-${p.sku}`}
            imageUrl={p.image}
            title={`${formatInt(p.quantity)} × ${p.name}`}
            value={formatMoney(p.cogs)}
            leftMeta={
              p.quantity > 0
                ? `${formatMoney(Math.round(p.cogs / p.quantity))} ${t('demo.labels.costPerUnit')}`
                : undefined
            }
            dimQuantityPrefix
          />
        ))}
      </div>
    </div>
  )

  /** Business Costs hover panel content */
  const renderBusinessCostsHoverContent = (): ReactNode => (
    <div>
      <HoverInfoHeader
        title={t('demo.businessCosts.title')}
        leading={
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: 'var(--chart-legend-overhead)' }}
            aria-hidden="true"
          />
        }
        leadingClassName="self-center"
      />
      <HoverInfoDivider />
      <div>
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border/30">
          <span className="text-sm text-foreground/70">{t('demo.businessCosts.shipping')}</span>
          <span className="font-mono text-sm font-medium">15.8K zł</span>
        </div>
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border/30">
          <span className="text-sm text-foreground/70">{t('demo.businessCosts.warehouse')}</span>
          <span className="font-mono text-sm font-medium">10.9K zł</span>
        </div>
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border/30">
          <span className="text-sm text-foreground/70">{t('demo.businessCosts.paymentFees')}</span>
          <span className="font-mono text-sm font-medium">7.9K zł</span>
        </div>
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border/30">
          <span className="text-sm text-foreground/70">{t('demo.businessCosts.software')}</span>
          <span className="font-mono text-sm font-medium">6.0K zł</span>
        </div>
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border/30">
          <span className="text-sm text-foreground/70">{t('demo.businessCosts.accounting')}</span>
          <span className="font-mono text-sm font-medium">4.0K zł</span>
        </div>
        <div className="flex items-center justify-between gap-4 px-6 py-3">
          <span className="text-sm text-foreground/70">{t('demo.businessCosts.officeTools')}</span>
          <span className="font-mono text-sm font-medium">2.5K zł</span>
        </div>
      </div>
      <div className="flex items-center justify-between px-6 pt-2 pb-3 bg-black/10 border-t border-border">
        <div className="flex items-center gap-2">
          <div
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: 'var(--chart-legend-overhead)' }}
          />
          <span className="text-sm text-foreground/80">{t('demo.labels.total')}</span>
        </div>
        <span className="font-mono text-sm font-semibold text-foreground/70">47.1K zł</span>
      </div>
    </div>
  )

  /** Profit hover panel content - stacks vertically on mobile/tablet, 2-col on desktop */
  const renderProfitHoverContent = (): ReactNode => (
    <div className="pb-3">
      {isBelowDesktop ? (
        <>
          {/* Most profitable section */}
          <div className="flex items-center justify-between bg-black/30 border-b border-border px-4 py-3">
            <p className="text-lg font-semibold">
              {t('demo.labels.mostProfitable')}
            </p>
            <ExpandIcon />
          </div>
          <div className="border-b border-border/50">
            {MOCK_PRODUCTS.profitable.map(p => (
              <HoverProductRow
                key={`gain-${p.sku}`}
                imageUrl={p.image}
                title={`${formatInt(p.quantity)} × ${p.name}`}
                value={`+${formatMoney(p.profit)}`}
                valueClassName="text-[var(--color-positive)]"
                leftMeta={`${formatMoney(p.adSpend)} ${t('demo.labels.adSpent')}`}
                dimQuantityPrefix
              />
            ))}
          </div>
          {/* Money losers section */}
          <div className="flex items-center justify-between bg-black/30 border-b border-border px-4 py-3">
            <p className="text-lg font-semibold">
              {t('demo.labels.moneyLosers')}
            </p>
            <ExpandIcon />
          </div>
          <div>
            {MOCK_PRODUCTS.losers.map(p => (
              <HoverProductRow
                key={`loss-${p.sku}`}
                imageUrl={p.image}
                title={`${formatInt(p.quantity)} × ${p.name}`}
                value={`-${formatMoney(Math.abs(p.profit))}`}
                valueClassName="text-[var(--color-negative)]"
                leftMeta={`${formatMoney(p.adSpend)} ${t('demo.labels.adSpent')}`}
                dimQuantityPrefix
              />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Desktop: 2-column layout */}
          <div className="grid grid-cols-2 bg-black/30 border-b border-border">
            <div className="flex items-center justify-between py-4 px-6">
              <p className="text-xl font-semibold">
                {t('demo.labels.mostProfitable')}
              </p>
              <ExpandIcon />
            </div>
            <div className="flex items-center justify-between py-4 px-6">
              <p className="text-xl font-semibold">
                {t('demo.labels.moneyLosers')}
              </p>
              <ExpandIcon />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div>
              {MOCK_PRODUCTS.profitable.map(p => (
                <HoverProductRow
                  key={`gain-${p.sku}`}
                  imageUrl={p.image}
                  title={`${formatInt(p.quantity)} × ${p.name}`}
                  value={`+${formatMoney(p.profit)}`}
                  valueClassName="text-[var(--color-positive)]"
                  leftMeta={`${formatMoney(p.adSpend)} ${t('demo.labels.adSpent')}`}
                  dimQuantityPrefix
                />
              ))}
            </div>
            <div>
              {MOCK_PRODUCTS.losers.map(p => (
                <HoverProductRow
                  key={`loss-${p.sku}`}
                  imageUrl={p.image}
                  title={`${formatInt(p.quantity)} × ${p.name}`}
                  value={`-${formatMoney(Math.abs(p.profit))}`}
                  valueClassName="text-[var(--color-negative)]"
                  leftMeta={`${formatMoney(p.adSpend)} ${t('demo.labels.adSpent')}`}
                  dimQuantityPrefix
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
      {/* CARD 1: SALES TOTAL */}
      <div className="h-full">
        <ResponsiveInfoPanel
          trigger={
            <AnimatedCard variant="subtle" className="h-full">
              <div className="glass-card !p-0 cursor-pointer flex flex-col h-full">
                <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 bg-black/20">
                  <div className="text-3xl md:text-4xl font-semibold font-mono tracking-tight animated-card-value">
                    {formatMoney(MOCK_METRICS.revenue)}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-muted-foreground">
                    <ShoppingCart className="h-5 w-5" />
                    <span className="text-sm font-medium">{t('demo.metrics.salesTotal')}</span>
                  </div>
                </div>
                <div className="border-t border-border/30 px-4 py-2.5 flex items-center justify-center min-h-[53px] hover:bg-black/40 transition-colors">
                  <p className="text-xs text-muted-foreground text-center">
                    {formatInt(MOCK_METRICS.orders)} {t('demo.metrics.orders')} · {formatMoney(MOCK_METRICS.avgOrderValue)} {t('demo.metrics.avg')}
                  </p>
                </div>
              </div>
            </AnimatedCard>
          }
          title={t('demo.panelTitles.salesDetails')}
          align="center"
          side="top"
          size="one"
        >
          {renderSalesHoverContent()}
        </ResponsiveInfoPanel>
      </div>

      {/* CARD 2: TOTAL COSTS */}
      <AnimatedCard variant="subtle" className="h-full">
        <div className="glass-card !p-0 flex flex-col h-full">
          {/* Primary zone - triggers ad spend panel */}
          <ResponsiveInfoPanel
            trigger={
              <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 bg-black/20 cursor-pointer hover:bg-black/30 transition-colors">
                <div className="text-3xl md:text-4xl font-semibold font-mono tracking-tight animated-card-value">
                  {formatMoney(MOCK_METRICS.totalCosts)}
                </div>
                <div className="flex items-center gap-2 mt-1.5 text-muted-foreground">
                  <BanknoteArrowDown className="h-5 w-5" />
                  <span className="text-sm font-medium">{t('demo.metrics.totalCosts')}</span>
                </div>
              </div>
            }
            title={t('demo.panelTitles.adSpendDetails')}
            align="center"
            side="top"
            size="one"
          >
            {renderAdSpendHoverContent()}
          </ResponsiveInfoPanel>
          <div className="border-t border-border/30 grid grid-cols-3 divide-x divide-border/30">
            {/* Ad Spend segment */}
            <ResponsiveInfoPanel
              trigger={
                <div className="flex flex-col items-center justify-center py-2.5 px-2 cursor-pointer hover:bg-black/40 transition-colors">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {t('demo.metrics.adSpend')}
                  </span>
                  <span className="text-xs font-medium font-mono mt-0.5">
                    {formatMoney(MOCK_METRICS.adSpend)}
                  </span>
                </div>
              }
              title={t('demo.panelTitles.adSpendDetails')}
              align="center"
              side="bottom"
              size="one"
            >
              {renderAdSpendHoverContent()}
            </ResponsiveInfoPanel>

            {/* COGS segment */}
            <ResponsiveInfoPanel
              trigger={
                <div className="flex flex-col items-center justify-center py-2.5 px-2 cursor-pointer hover:bg-black/40 transition-colors">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {t('demo.metrics.cogs')}
                  </span>
                  <span className="text-xs font-medium font-mono mt-0.5">
                    {formatMoney(MOCK_METRICS.cogs)}
                  </span>
                </div>
              }
              title={t('demo.panelTitles.cogsDetails')}
              align="center"
              side="bottom"
              size="one"
            >
              {renderCogsHoverContent()}
            </ResponsiveInfoPanel>

            {/* Business Costs segment */}
            <ResponsiveInfoPanel
              trigger={
                <div className="flex flex-col items-center justify-center py-2.5 px-2 cursor-pointer hover:bg-black/40 transition-colors">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {t('demo.metrics.bizCosts')}
                  </span>
                  <span className="text-xs font-medium font-mono mt-0.5">
                    {formatMoney(MOCK_METRICS.businessCosts)}
                  </span>
                </div>
              }
              title={t('demo.panelTitles.businessCosts')}
              align="center"
              side="bottom"
              size="one"
            >
              {renderBusinessCostsHoverContent()}
            </ResponsiveInfoPanel>
          </div>
        </div>
      </AnimatedCard>

      {/* CARD 3: PROFIT */}
      <div className="h-full">
        <ResponsiveInfoPanel
          trigger={
            <AnimatedCard variant="subtle" className="h-full">
              <div className="glass-card !p-0 cursor-pointer flex flex-col h-full relative overflow-hidden">
                <BackgroundGradientAnimation
                  {...GRADIENT_THEMES.positive}
                  containerClassName="absolute inset-0 opacity-50 pointer-events-none"
                  interactive={false}
                  size="120%"
                />
                <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 bg-black/20 relative z-10">
                  <div className={cn(
                    'text-3xl md:text-4xl font-semibold font-mono tracking-tight animated-card-value',
                    'text-positive'
                  )}>
                    +{formatMoney(MOCK_METRICS.profit)}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-muted-foreground">
                    <CircleDollarSign className="h-5 w-5" />
                    <span className="text-sm font-medium">{t('demo.metrics.profit')}</span>
                  </div>
                </div>
                <div className="border-t border-border/30 px-4 py-2.5 relative z-10 flex items-center justify-center min-h-[53px] hover:bg-black/40 transition-colors">
                  <p className="text-xs text-muted-foreground text-center">
                    +{MOCK_METRICS.margin.toFixed(1)}% {t('demo.metrics.margin')}
                  </p>
                </div>
              </div>
            </AnimatedCard>
          }
          title={t('demo.panelTitles.profitDetails')}
          align="center"
          side="top"
          size="two"
        >
          {renderProfitHoverContent()}
        </ResponsiveInfoPanel>
      </div>
      </div>
      <p className="text-xs text-foreground/70 text-center mt-2">{t('demo.clickHint')}</p>
    </div>
  )
}
